"use client";

import SimpleRichText from "@components/SimpleRichText";
import Section, { Ground } from "@modules/homepage-v2/components/Section";
import SectionHead from "@modules/homepage-v2/components/SectionHead";
import { FaqAccordionSection } from "@modules/homepage-v2/lib/getSections";
import { useId, useState } from "react";

const Plus = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className="w-[20px] h-[20px] flex-none text-neutral-425"
  >
    <path
      d="M4 12h16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M12 4v16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className={`origin-center transition-transform duration-150 motion-reduce:transition-none ${
        open ? "scale-y-0" : ""
      }`}
    />
  </svg>
);

const FaqAccordion = ({
  section,
  ground,
}: {
  section: FaqAccordionSection;
  ground: Ground;
}) => {
  const prefix = useId();

  const faqs = (section.faqs?.data ?? []).flatMap((entry) => {
    const faq = entry.attributes as
      | { question?: string; answer?: string }
      | undefined;

    return entry.id && faq?.question
      ? [{ id: entry.id, question: faq.question, answer: faq.answer ?? null }]
      : [];
  });

  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    faqs.length ? { [faqs[0]!.id]: true } : {},
  );

  if (!faqs.length || !section.headline) {
    return null;
  }

  return (
    <Section ground={ground}>
      <SectionHead headline={section.headline} subline={section.subline} />

      <div className="mt-2m grid border-b border-neutral-50">
        {faqs.map((faq) => {
          const expanded = !!open[faq.id];
          const buttonId = `${prefix}-q-${faq.id}`;
          const regionId = `${prefix}-a-${faq.id}`;

          return (
            <div key={faq.id} className="border-t border-neutral-50">
              <h3 className="m-none">
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={expanded}
                  aria-controls={regionId}
                  onClick={() =>
                    setOpen((current) => ({
                      ...current,
                      [faq.id]: !current[faq.id],
                    }))
                  }
                  className="w-full flex items-center justify-between gap-3s py-3s text-left cursor-pointer bg-transparent border-none p-none text-h5 font-semibold text-neutral-675 focusable"
                >
                  {faq.question}
                  <Plus open={expanded} />
                </button>
              </h3>

              <div
                id={regionId}
                role="region"
                aria-labelledby={buttonId}
                inert={!expanded}
                className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
                  expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  {faq.answer && (
                    <SimpleRichText className="text-body-1 font-book text-neutral-425 max-w-[64ch] pb-3s [&_p]:m-none">
                      {faq.answer}
                    </SimpleRichText>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export default FaqAccordion;
